import { apiGet, apiPost } from './api/grampsApi';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const PROFILES_KEY = 'gramps_auth_profiles_v1';
const ACTIVE_PROFILE_ID_KEY = 'gramps_auth_active_profile_id_v1';
const OIDC_STORAGE_PREFERENCE_KEY = 'gramps_oidc_storage_preference_v1';

type TokenStorage = 'local' | 'session';

export type GrampsAuthProfile = {
  id: string;
  username: string;
  label: string;
  storage: TokenStorage;
  accessToken: string;
  refreshToken?: string;
};

function normalizeTreeName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
}

function findProfileByTreeName(treeName: string | null | undefined): GrampsAuthProfile | null {
  const normalizedTreeName = normalizeTreeName(treeName);
  if (!normalizedTreeName) {
    return null;
  }

  const profiles = readProfilesFromStorage();
  const exactMatch = profiles.find((profile) => normalizeTreeName(profile.label) === normalizedTreeName)
  if (exactMatch) {
    return exactMatch;
  }

  return profiles.find((profile) => normalizeTreeName(profile.username) === normalizedTreeName) || null;
}

function isLocalhostHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function supportsSharedLocalCookies(): boolean {
  return isLocalhostHost(window.location.hostname);
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
  try {
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function parseJwtExp(token: string): number | null {
  const parsed = parseJwtPayload(token);
  if (!parsed) return null;
  if (typeof parsed.exp !== 'number' || !Number.isFinite(parsed.exp)) return null;
  return parsed.exp;
}

function makeProfileId(username: string, token: string): string {
  const payload = parseJwtPayload(token);
  const sub = typeof payload?.sub === 'string' ? payload.sub.trim() : '';
  if (sub) {
    return `${username.trim().toLowerCase()}::${sub}`;
  }
  return `${username.trim().toLowerCase()}::${token.slice(0, 12)}`;
}

function readProfilesFromStorage(): GrampsAuthProfile[] {
  try {
    const raw = window.localStorage.getItem(PROFILES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is GrampsAuthProfile => {
      if (!item || typeof item !== 'object') return false;
      const obj = item as Record<string, unknown>;
      return (
        typeof obj.id === 'string' &&
        typeof obj.username === 'string' &&
        typeof obj.label === 'string' &&
        (obj.storage === 'local' || obj.storage === 'session') &&
        typeof obj.accessToken === 'string' &&
        (obj.refreshToken === undefined || typeof obj.refreshToken === 'string')
      );
    });
  } catch {
    return [];
  }
}

const bearerTokenListeners = new Set<() => void>();

/**
 * Every path that can change what readBearerToken()/readRefreshToken() return
 * (login, logout, profile switch, the background auto-refresh timer, a 401-triggered
 * refresh) funnels through upsertProfile/removeProfileById/updateProfileTokensById,
 * which all write here. Notifying from this single choke point means callers that
 * need to react to token changes (e.g. media URLs baked into <img>/<video> src,
 * which can't carry an Authorization header) don't need to poll storage themselves.
 */
function writeProfilesToStorage(profiles: GrampsAuthProfile[]): void {
  window.localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
  bearerTokenListeners.forEach((listener) => listener());
}

/**
 * Subscribes to any change in the active (or any) auth profile's tokens.
 * Intended for useSyncExternalStore in components that derive a value (e.g. a
 * media URL with the token baked in as a query param) from readBearerToken().
 */
export function subscribeToBearerToken(listener: () => void): () => void {
  bearerTokenListeners.add(listener);
  return () => {
    bearerTokenListeners.delete(listener);
  };
}

function getActiveProfileId(): string | null {
  const value = window.localStorage.getItem(ACTIVE_PROFILE_ID_KEY);
  return value?.trim() || null;
}

function setActiveProfileId(id: string | null): void {
  if (!id) {
    window.localStorage.removeItem(ACTIVE_PROFILE_ID_KEY);
    return;
  }
  window.localStorage.setItem(ACTIVE_PROFILE_ID_KEY, id);
}

function getActiveProfileInternal(): GrampsAuthProfile | null {
  const profiles = readProfilesFromStorage();
  if (profiles.length === 0) return null;

  const activeId = getActiveProfileId();
  if (!activeId) {
    return profiles[0] || null;
  }
  return profiles.find((profile) => profile.id === activeId) || profiles[0] || null;
}

function upsertProfile(profile: GrampsAuthProfile): void {
  const profiles = readProfilesFromStorage();
  const idx = profiles.findIndex((item) => item.id === profile.id);
  if (idx >= 0) {
    profiles[idx] = profile;
  } else {
    profiles.push(profile);
  }
  writeProfilesToStorage(profiles);
  setActiveProfileId(profile.id);
}

function removeProfileById(profileId: string): void {
  const profiles = readProfilesFromStorage().filter((item) => item.id !== profileId);
  writeProfilesToStorage(profiles);
  const activeId = getActiveProfileId();
  if (activeId === profileId) {
    setActiveProfileId(profiles[0]?.id || null);
  }
}

async function fetchProfileLabel(username: string, token: string): Promise<string> {
  const base = username.trim();
  try {
    const meta = await apiGet('/api/metadata/', { token }) as Record<string, unknown> | null;
    if (meta) {
      const db = meta.database as Record<string, unknown> | undefined;
      const treeName =
        (typeof meta.tree_name === 'string' && meta.tree_name.trim()) ||
        (typeof meta.title === 'string' && meta.title.trim()) ||
        (db && typeof db.name === 'string' && db.name.trim()) ||
        '';
      if (treeName) {
        return treeName;
      }
    }
  } catch {
    // fall through
  }
  return base;
}

function migrateLegacySingleProfileIfNeeded(): void {
  const profiles = readProfilesFromStorage();
  if (profiles.length > 0) {
    return;
  }

  const access = readTokenWithStorage(ACCESS_TOKEN_KEY);
  if (!access.token) {
    return;
  }

  const refresh = readTokenWithStorage(REFRESH_TOKEN_KEY);
  const payload = parseJwtPayload(access.token);
  const username = typeof payload?.preferred_username === 'string' && payload.preferred_username.trim()
    ? payload.preferred_username.trim()
    : 'Compte';
  const profile: GrampsAuthProfile = {
    id: makeProfileId(username, access.token),
    username,
    label: username,
    storage: access.storage || refresh.storage || 'local',
    accessToken: access.token,
    refreshToken: refresh.token || undefined,
  };

  upsertProfile(profile);
}

function readActiveProfileToken(key: 'accessToken' | 'refreshToken'): { token: string | null; storage: TokenStorage | null } {
  migrateLegacySingleProfileIfNeeded();
  const active = getActiveProfileInternal();
  if (!active) {
    return { token: null, storage: null };
  }
  const token = key === 'accessToken' ? active.accessToken : active.refreshToken || null;
  return { token, storage: active.storage };
}

function updateActiveProfileTokens(tokens: { accessToken?: string | null; refreshToken?: string | null }): void {
  migrateLegacySingleProfileIfNeeded();
  const active = getActiveProfileInternal();
  if (!active) {
    return;
  }

  const next: GrampsAuthProfile = {
    ...active,
    accessToken: tokens.accessToken ?? active.accessToken,
    refreshToken: tokens.refreshToken === undefined ? active.refreshToken : tokens.refreshToken || undefined,
  };
  upsertProfile(next);
}

export function listGrampsProfiles(): GrampsAuthProfile[] {
  migrateLegacySingleProfileIfNeeded();
  return readProfilesFromStorage();
}

export function getActiveGrampsProfileId(): string | null {
  migrateLegacySingleProfileIfNeeded();
  const active = getActiveProfileInternal();
  return active?.id || null;
}

export function setActiveGrampsProfile(profileId: string): boolean {
  migrateLegacySingleProfileIfNeeded();
  const profiles = readProfilesFromStorage();
  const exists = profiles.some((profile) => profile.id === profileId);
  if (!exists) {
    return false;
  }
  setActiveProfileId(profileId);
  return true;
}

export function readCurrentUserUuid(): string | null {
  const token = readBearerToken();
  if (!token) return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  const sub = payload.sub;
  return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
}

export async function fetchCurrentUser(): Promise<string | null> {
  const token = readBearerToken();
  if (!token) return null;
  const userId = readCurrentUserUuid();
  if (!userId) return null;
  try {
    const rows = await apiGet('/api/users/', {
      token,
      query: { user_id: userId },
    }) as Array<{ full_name?: string; username?: string; name?: string }> | null;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const user = rows[0];
    return (
      (typeof user.full_name === 'string' && user.full_name.trim() ? user.full_name.trim() : null) ??
      (typeof user.username === 'string' && user.username.trim() ? user.username.trim() : null) ??
      (typeof user.name === 'string' && user.name.trim() ? user.name.trim() : null)
    );
  } catch {
    return null;
  }
}

function writeHostCookie(name: string, value: string, storage: TokenStorage): void {
  if (!supportsSharedLocalCookies()) {
    return;
  }

  const pieces = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    'SameSite=Lax',
  ];

  if (storage === 'local') {
    const exp = parseJwtExp(value);
    if (exp && exp > 0) {
      const now = Math.floor(Date.now() / 1000);
      const maxAge = Math.max(0, exp - now);
      pieces.push(`Max-Age=${maxAge}`);
    } else {
      pieces.push(`Max-Age=${30 * 24 * 60 * 60}`);
    }
  }

  document.cookie = pieces.join('; ');
}

function deleteHostCookie(name: string): void {
  if (!supportsSharedLocalCookies()) {
    return;
  }
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function readHostCookie(name: string): string | null {
  if (!supportsSharedLocalCookies()) {
    return null;
  }

  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((v) => v.trim())
    .find((v) => v.startsWith(prefix));
  if (!entry) return null;

  const raw = entry.slice(prefix.length);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function readTokenWithStorage(key: string): { token: string | null; storage: TokenStorage | null } {
  const fromLocal = window.localStorage.getItem(key);
  if (fromLocal?.trim()) {
    return { token: fromLocal.trim(), storage: 'local' };
  }

  const fromSession = window.sessionStorage.getItem(key);
  if (fromSession?.trim()) {
    return { token: fromSession.trim(), storage: 'session' };
  }

  return { token: null, storage: null };
}

export function getGrampsApiBase(): string {
  const envBase = (import.meta.env.VITE_GRAMPS_API_BASE_URL as string | undefined)?.trim();
  if (envBase) {
    return envBase.replace(/\/+$/, '');
  }

  return isLocalhostHost(window.location.hostname)
    ? 'http://localhost:5000/api'
    : 'https://backend.genealhistory.eu/api';
}

export function getGrampsBackendBase(): string {
  return isLocalhostHost(window.location.hostname)
    ? 'http://localhost:5000'
    : 'https://backend.genealhistory.eu';
}

// marchand-viveret.eu is the public-facing cousinade site: visitors there
// shouldn't see links exposing the Gramps backend's internal record IDs.
export function isMarchandViveretHost(hostname: string = window.location.hostname): boolean {
  return hostname === 'marchand-viveret.eu' || hostname === 'www.marchand-viveret.eu';
}

export function readBearerToken(treeName?: string | null): string | null {
  const matchingProfile = findProfileByTreeName(treeName);
  if (matchingProfile?.accessToken) {
    return matchingProfile.accessToken;
  }

  const token = readActiveProfileToken('accessToken').token;
  if (token) return token;
  return readHostCookie(ACCESS_TOKEN_KEY);
}

// Matches PERM_USE_CHAT in gramps-webapi's auth/const.py. The backend only adds
// it to a token's `permissions` claim if the user's role meets the tree's
// min_role_ai at token-issue time, so this is a client-side mirror of a
// decision already made server-side -- it just lets the UI hide the chat
// entry point instead of showing it and failing with a 403 on first use.
export const PERM_USE_CHAT = 'UseChat';

export function hasBearerPermission(permission: string, treeName?: string | null): boolean {
  const token = readBearerToken(treeName);
  if (!token) return false;
  const payload = parseJwtPayload(token);
  const permissions = payload?.permissions;
  return Array.isArray(permissions) && permissions.includes(permission);
}

export function readRefreshToken(treeName?: string | null): string | null {
  const matchingProfile = findProfileByTreeName(treeName);
  if (matchingProfile?.refreshToken) {
    return matchingProfile.refreshToken;
  }

  const token = readActiveProfileToken('refreshToken').token;
  if (token) return token;
  return readHostCookie(REFRESH_TOKEN_KEY);
}

export function storeBearerToken(token: string, storage: TokenStorage = 'local'): void {
  updateActiveProfileTokens({ accessToken: token });

  const target = storage === 'local' ? window.localStorage : window.sessionStorage;
  const secondary = storage === 'local' ? window.sessionStorage : window.localStorage;
  target.setItem(ACCESS_TOKEN_KEY, token);
  secondary.removeItem(ACCESS_TOKEN_KEY);
  writeHostCookie(ACCESS_TOKEN_KEY, token, storage);
}

export function storeRefreshToken(token: string, storage: TokenStorage = 'local'): void {
  updateActiveProfileTokens({ refreshToken: token });

  const target = storage === 'local' ? window.localStorage : window.sessionStorage;
  const secondary = storage === 'local' ? window.sessionStorage : window.localStorage;

  target.setItem(REFRESH_TOKEN_KEY, token);
  secondary.removeItem(REFRESH_TOKEN_KEY);
  writeHostCookie(REFRESH_TOKEN_KEY, token, storage);
}

export function clearBearerToken(): void {
  const activeId = getActiveGrampsProfileId();
  if (activeId) {
    removeProfileById(activeId);
  }

  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);

  deleteHostCookie(ACCESS_TOKEN_KEY);
  deleteHostCookie(REFRESH_TOKEN_KEY);
}

async function loginWithCredentials(
  _baseUrl: string,
  username: string,
  password: string,
): Promise<{ accessToken: string; refreshToken?: string } | null> {
  const payload = await apiPost('/api/token/', {
    body: { username, password },
  }) as { access_token?: string; refresh_token?: string } | null;
  if (!payload) return null;
  if (!payload.access_token) return null;

  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
  };
}

async function refreshAccessToken(baseUrl: string, refreshToken: string): Promise<string | null> {
  void baseUrl;
  const payload = await apiPost('/api/token/refresh/', {
    token: refreshToken,
  }) as { access_token?: string } | null;
  if (!payload) return null;
  return payload.access_token || null;
}

export async function tokenLooksValid(_baseUrl: string, token: string): Promise<boolean> {
  try {
    const response = await apiGet('/api/events/', {
      token,
      query: {
        page: 1,
        pagesize: 1,
        keys: ['handle'],
      },
    });
    return !!response;
  } catch {
    return false;
  }
}

function findProfileByAccessToken(token: string): GrampsAuthProfile | null {
  return readProfilesFromStorage().find((profile) => profile.accessToken === token) || null;
}

function updateProfileTokensById(profileId: string, tokens: { accessToken: string }): void {
  const profiles = readProfilesFromStorage();
  const idx = profiles.findIndex((profile) => profile.id === profileId);
  if (idx < 0) return;
  profiles[idx] = { ...profiles[idx], accessToken: tokens.accessToken };
  writeProfilesToStorage(profiles);
}

/**
 * Refreshes the profile that a failed request actually belongs to, not just whichever
 * profile is "active". A 401 from a background tab/tree that isn't the active profile
 * would otherwise be judged against the active profile's (unrelated) token validity,
 * so a healthy active session could mask a genuinely dead one and the login dialog
 * would never open for it.
 */
export async function ensureGrampsToken(failedToken?: string): Promise<string | null> {
  const baseUrl = getGrampsApiBase();
  migrateLegacySingleProfileIfNeeded();

  const targetProfile = (failedToken ? findProfileByAccessToken(failedToken) : null) ?? getActiveProfileInternal();
  if (!targetProfile) {
    clearBearerToken();
    return null;
  }

  if (targetProfile.accessToken) {
    const valid = await tokenLooksValid(baseUrl, targetProfile.accessToken);
    if (valid) return targetProfile.accessToken;
  }

  if (targetProfile.refreshToken) {
    const freshAccessToken = await refreshAccessToken(baseUrl, targetProfile.refreshToken);
    if (freshAccessToken) {
      if (targetProfile.id === getActiveGrampsProfileId()) {
        storeBearerToken(freshAccessToken, targetProfile.storage);
      } else {
        updateProfileTokensById(targetProfile.id, { accessToken: freshAccessToken });
      }
      return freshAccessToken;
    }
  }

  if (targetProfile.id === getActiveGrampsProfileId()) {
    clearBearerToken();
  } else {
    removeProfileById(targetProfile.id);
  }

  return null;
}

async function finalizeLogin(
  username: string,
  accessToken: string,
  refreshToken: string | undefined,
  storage: TokenStorage,
): Promise<string> {
  const treeName = await fetchProfileLabel(username, accessToken);
  const profile: GrampsAuthProfile = {
    id: makeProfileId(username, accessToken),
    username,
    label: treeName,
    storage,
    accessToken,
    refreshToken,
  };
  upsertProfile(profile);

  storeBearerToken(accessToken, storage);
  if (refreshToken) {
    storeRefreshToken(refreshToken, storage);
  }
  return accessToken;
}

export async function loginAndStoreGrampsToken(
  username: string,
  password: string,
  storage: TokenStorage = 'local',
): Promise<string | null> {
  const baseUrl = getGrampsApiBase();

  if (!username.trim() || !password.trim()) {
    return null;
  }

  const fresh = await loginWithCredentials(baseUrl, username.trim(), password);
  if (!fresh) return null;

  return finalizeLogin(username.trim(), fresh.accessToken, fresh.refreshToken, storage);
}

export type OidcProvider = {
  id: string;
  name: string;
  loginUrl: string;
};

/**
 * Only Google is wired up on the backend today, but this reads whatever
 * `/api/oidc/config/` advertises instead of hardcoding 'google', so a
 * provider added there later shows up without a frontend change.
 */
export async function fetchOidcProviders(): Promise<OidcProvider[]> {
  try {
    const config = await apiGet('/api/oidc/config/') as {
      enabled?: boolean;
      providers?: { id?: string; name?: string; login_url?: string }[];
    } | null;
    if (!config?.enabled || !Array.isArray(config.providers)) return [];
    return config.providers
      .filter((provider): provider is { id: string; name: string; login_url: string } =>
        !!provider.id && !!provider.name && !!provider.login_url)
      .map((provider) => ({ id: provider.id, name: provider.name, loginUrl: provider.login_url }));
  } catch {
    return [];
  }
}

/**
 * The OIDC round trip leaves the page entirely (provider consent screen, then
 * the backend's own redirect), so the "remember me" choice can't survive in
 * React state. Stashed in sessionStorage instead, and consumed once by
 * completeOidcLogin() back on /oidc/complete.
 */
function rememberOidcStoragePreference(storage: TokenStorage): void {
  window.sessionStorage.setItem(OIDC_STORAGE_PREFERENCE_KEY, storage);
}

function consumeOidcStoragePreference(): TokenStorage {
  const value = window.sessionStorage.getItem(OIDC_STORAGE_PREFERENCE_KEY);
  window.sessionStorage.removeItem(OIDC_STORAGE_PREFERENCE_KEY);
  return value === 'session' ? 'session' : 'local';
}

/**
 * Starts the OIDC flow with a full-page navigation (not a fetch): the
 * provider's consent screen and the backend's own redirect back are both
 * outside this SPA. `treeName` scopes which tree a *new* account is created
 * under; an existing account's tree is resolved server-side regardless.
 */
export function startOidcLogin(loginUrl: string, treeName: string | null | undefined, storage: TokenStorage): void {
  rememberOidcStoragePreference(storage);
  const url = new URL(loginUrl);
  if (treeName) {
    url.searchParams.set('tree', treeName);
  }
  window.location.assign(url.toString());
}

/**
 * Redeems the HTTP-only cookies the backend's /oidc/callback/ set (they expire
 * after 5 minutes) for JWTs, and stores them the same way a password login
 * would. Called once, from the /oidc/complete route the backend redirects to.
 */
export async function completeOidcLogin(): Promise<string | null> {
  const storage = consumeOidcStoragePreference();
  const payload = await apiGet('/api/oidc/tokens/') as {
    access_token?: string;
    refresh_token?: string;
  } | null;
  if (!payload?.access_token) return null;

  const claims = parseJwtPayload(payload.access_token);
  const username =
    (typeof claims?.preferred_username === 'string' && claims.preferred_username.trim()) ||
    (typeof claims?.email === 'string' && claims.email.trim()) ||
    'Compte Google';

  return finalizeLogin(username, payload.access_token, payload.refresh_token, storage);
}

export function startGrampsTokenAutoRefresh(
  onAuthLost: () => void,
  intervalMs: number = Number(import.meta.env.VITE_GRAMPS_TOKEN_REFRESH_MS || 10 * 60 * 1000),
): () => void {
  let stopped = false;
  let refreshing = false;

  const tick = async () => {
    if (stopped || refreshing) {
      return;
    }

    const refreshToken = readRefreshToken();
    if (!refreshToken) {
      return;
    }

    refreshing = true;
    try {
      const baseUrl = getGrampsApiBase();
      const freshAccessToken = await refreshAccessToken(baseUrl, refreshToken);
      if (!freshAccessToken) {
        clearBearerToken();
        onAuthLost();
        return;
      }

      const { storage: refreshStorage } = readActiveProfileToken('refreshToken');
      updateActiveProfileTokens({ accessToken: freshAccessToken });
      storeBearerToken(freshAccessToken, refreshStorage || 'local');
    } finally {
      refreshing = false;
    }
  };

  const intervalId = window.setInterval(() => {
    void tick();
  }, Math.max(30_000, intervalMs));

  return () => {
    stopped = true;
    window.clearInterval(intervalId);
  };
}
